import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Intent } from '@Components/designConstants';
import { getCodeFriendlyMessage, getFriendlyErrorMessage } from '../utils/error-code-messages';
import { classifyModalError, referenceExtra } from '../utils/error-reference';
import { reportError } from '../utils/errorReporting';
import { findFetchError } from '../utils/fetch-error';
import { AppDispatch, RootState } from '.';
import {
  ErrorParams,
  ErrorSource,
  ModalAreYouSure,
  ModalError,
  SetAreYouSure,
  SetError
} from './modals-types';
import { makeReducer } from './utils';

export type { ErrorParams, ModalAreYouSure, ModalError, SetAreYouSure, SetError };

const ROOT = 'modals';
const SET_ERROR = `${ROOT}/SET_ERROR`;
const SET_ARE_YOU_SURE = `${ROOT}/SET_ARE_YOU_SURE`;
const CLEAR_MODALS = `${ROOT}/CLEAR_MODALS`;

interface ModalReduxState {
  error?: ModalError | null;
  areYouSure?: ModalAreYouSure | null;
}

export const dispatchError = (dispatch: AppDispatch, error: ModalError): Promise<void> =>
  new Promise<void>(resolve => {
    // Source and reference ID come from classifyModalError.
    const { source, id } = classifyModalError(error);
    dispatch({
      type: SET_ERROR,
      error: {
        ...error,
        errorSource: source,
        referenceId: id,
        onOkay: () => {
          if (error.onOkay) {
            error.onOkay();
          }
          resolve();
        }
      }
    });
  });

export const useSetError = (): SetError => {
  const dispatch = useDispatch<AppDispatch>();
  const setError = useCallback((error: ModalError) => dispatchError(dispatch, error), [dispatch]);
  return setError;
};

export const useSetAreYouSure = (): SetAreYouSure => {
  const dispatch = useDispatch<AppDispatch>();
  const setAreYouSure = useCallback(
    (areYouSure: ModalAreYouSure) =>
      new Promise<void>((resolve, reject) => {
        dispatch({
          type: SET_ARE_YOU_SURE,
          areYouSure: {
            ...areYouSure,
            onOkay: () => {
              if (areYouSure.onOkay) {
                areYouSure.onOkay();
              }
              resolve();
            },
            onCancel: () => {
              if (areYouSure.onCancel) {
                areYouSure.onCancel();
              }
              reject();
            }
          }
        });
      }),
    [dispatch]
  );
  return setAreYouSure;
};

export const useModalInfo = (): {
  error: Partial<ModalError> | null | undefined;
  areYouSure: ModalAreYouSure | null | undefined;
} => {
  const dispatch = useDispatch<AppDispatch>();
  const clearModals = useCallback(() => {
    dispatch({
      type: CLEAR_MODALS
    });
  }, [dispatch]);

  const rawError = useSelector((state: RootState) => state.modals.error);
  const error = useMemo<Partial<ModalError> | null | undefined>(() => {
    if (!rawError) return rawError;
    return {
      title: 'Error',
      okayText: 'Okay',
      intent: Intent.ERROR,
      ...rawError,
      message: rawError.message ?? 'Error occurred',
      onOkay: () => {
        if (rawError.onOkay) {
          rawError.onOkay();
        }
        clearModals();
      }
    };
  }, [rawError, clearModals]);

  const rawAreYouSure = useSelector((state: RootState) => state.modals.areYouSure);
  const areYouSure = useMemo<ModalAreYouSure | null | undefined>(() => {
    if (!rawAreYouSure) return rawAreYouSure;
    return {
      title: 'Are you sure?',
      cancelText: 'Cancel',
      okayText: 'Okay',
      intent: Intent.WARNING,
      ...rawAreYouSure,
      message: rawAreYouSure.message ?? 'Are you sure?',
      onCancel: () => {
        if (rawAreYouSure.onCancel) {
          rawAreYouSure.onCancel();
        }
        clearModals();
      },
      onOkay: () => {
        if (rawAreYouSure.onOkay) {
          rawAreYouSure.onOkay();
        }
        clearModals();
      }
    };
  }, [rawAreYouSure, clearModals]);

  return {
    error,
    areYouSure
  };
};

const setError = (
  state: ModalReduxState,
  { error }: { error: ModalError & { errorSource: ErrorSource } }
) => {
  const fetchError = findFetchError(error.reportError);
  if (error.reportError) {
    console.error(error.message);
    // Routed through errorReporting.ts's reportError() (DEV-24753) like every other call site, now
    // keyed on the FetchError's own code/status (or the constructor name for a non-FetchError)
    // instead of one shared bucket for this reducer's ~199+ callers -- so throttling one kind of
    // error can't silently suppress reports for an unrelated one.
    // Every error is reported, 4xx included, tagged with its source and reference ID so a dialog's
    // ID can be searched in Datadog RUM.
    const { errorSource, referenceId } = error;
    let operation = 'modals.setError:non-fetch';
    // Bucket on the originating FetchError even when a caller wrapped it (via `cause`).
    if (fetchError) {
      operation = `modals.setError:${fetchError.code ?? fetchError.status}`;
    } else if (
      errorSource === 'gateway' ||
      errorSource === 'network' ||
      errorSource === 'timeout' ||
      errorSource === 'job'
    ) {
      operation = `modals.setError:${errorSource}`;
    }
    reportError(error.reportError, {
      operation: error.operationTag ? `${operation}:${error.operationTag}` : operation,
      fingerprint: errorSource === 'frontend' ? referenceId : undefined,
      extra: referenceExtra({ source: errorSource, id: referenceId })
    });
  }
  // Replace raw backend text with a friendly message for codes we can confidently classify from
  // HTTP semantics alone (DEV-24580), or with a generic fallback for any other 5xx (DEV-24578) --
  // an unrecognized 4xx falls through to the caller's own message unchanged, so this only upgrades
  // cases we have positive signal are safe to reword (a known code) or unsafe to show verbatim (a
  // server-side failure). The raw text + code are kept as rawMessage/code (only when an override
  // actually happened) so engineering/support can still see them via the dialog's "More info"
  // disclosure.
  const code = fetchError?.code;
  // The generic 5xx fallback never replaces a caller-authored message wholesale: in a wrapped
  // message ("Failed to get X. ERROR: <raw>") only the embedded raw text is swapped.
  const rawText = fetchError?.message;
  const isRawPassthrough = fetchError !== undefined && error.message === rawText;
  const codeMessage = fetchError ? getCodeFriendlyMessage(code, fetchError.status) : undefined;
  const genericMessage =
    fetchError && !codeMessage ? getFriendlyErrorMessage(undefined, fetchError.status) : undefined;
  let friendlyMessage = codeMessage;
  if (genericMessage && isRawPassthrough) {
    friendlyMessage = genericMessage;
  } else if (
    genericMessage &&
    rawText &&
    !/^\d+$/.test(rawText) &&
    typeof error.message === 'string'
  ) {
    friendlyMessage = error.message.includes(rawText)
      ? error.message.replaceAll(rawText, () => genericMessage)
      : undefined;
  }
  const wasOverridden = friendlyMessage !== undefined && typeof error.message === 'string';
  return {
    ...state,
    error: {
      ...error,
      message: friendlyMessage ?? error.message,
      rawMessage: wasOverridden ? (error.message as string) : undefined,
      code: wasOverridden ? code : undefined,
      reportError: undefined
    }
  };
};

const setAreYouSure = (
  state: ModalReduxState,
  { areYouSure }: { areYouSure: ModalAreYouSure }
) => ({
  ...state,
  areYouSure
});

const clearModals = (state: ModalReduxState) => ({
  ...state,
  error: null,
  areYouSure: null
});

export default makeReducer<ModalReduxState>(
  {},
  {
    [SET_ERROR]: setError,
    [SET_ARE_YOU_SURE]: setAreYouSure,
    [CLEAR_MODALS]: clearModals
  }
);
